import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/react";
import { ArrowRight, ArrowUpRight, Award, Brain as BrainIcon, Clapperboard, Pencil, Zap } from "lucide-react";
import { toast } from "sonner";
import { useApi, fileUrl } from "@/lib/api";
import { gradientFor } from "@/lib/sourceMeta";
import InterestGraph from "@/components/InterestGraph";

function Label({ children }) {
  return (
    <h2 className="text-center text-[11px] font-semibold uppercase tracking-[0.25em] text-stone-400">
      {children}
    </h2>
  );
}

export default function Home() {
  const api = useApi();
  const { user } = useUser();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef();

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => api.get("/me") });
  const { data: bio, isLoading: bioLoading } = useQuery({
    queryKey: ["biography"],
    queryFn: () => api.get("/biography"),
  });
  const { data: interests } = useQuery({ queryKey: ["interests"], queryFn: () => api.get("/interests") });
  const { data: archive } = useQuery({
    queryKey: ["entries", { archiveTop: true }],
    queryFn: () => api.get("/entries?type=archive&limit=3"),
  });
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/stats") });

  const avatar = fileUrl(me?.avatar_path) || user?.imageUrl;
  const name = user?.fullName || me?.name || "You";
  const first = (user?.firstName || me?.name || "").split(" ")[0];

  const uploadAvatar = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.upload("/me/avatar", fd);
      qc.invalidateQueries({ queryKey: ["me"] });
      toast.success("Photo updated");
    } catch {
      toast.error("Could not upload photo");
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-14">
      <section data-testid="hero-section" className="text-center pt-8 animate-fade-in">
        <button
          data-testid="avatar-button"
          onClick={() => fileRef.current?.click()}
          aria-label="Change profile photo"
          className="relative mx-auto rounded-full active:scale-95 transition-transform"
        >
          {avatar ? (
            <img
              data-testid="avatar-image"
              src={avatar}
              alt={name}
              className="h-32 w-32 rounded-full object-cover ring-4 ring-white/80 shadow-xl"
            />
          ) : (
            <div className="h-32 w-32 rounded-full bg-primary/10 ring-4 ring-white/80 shadow-xl flex items-center justify-center font-serif text-4xl text-primary">
              {(first || "B")[0].toUpperCase()}
            </div>
          )}
          <span className="absolute bottom-1 right-1 flex h-7 w-7 items-center justify-center rounded-full bg-stone-900/85 ring-2 ring-[#FAF8F5]">
            <Pencil className="h-3 w-3 text-white" />
          </span>
        </button>
        <input
          ref={fileRef}
          data-testid="avatar-input"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => uploadAvatar(e.target.files?.[0])}
        />
        <p className="mt-6 flex items-center justify-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
          <Clapperboard className="h-3 w-3" /> A short documentary of
        </p>
        <h1 data-testid="home-greeting" className="mt-3 font-serif text-4xl md:text-6xl tracking-tight">
          {name}
        </h1>
        {stats && (
          <p data-testid="home-stats" className="mt-3 text-xs font-medium text-stone-400">
            {stats.brain_count} {stats.brain_count === 1 ? "curiosity" : "curiosities"} ·{" "}
            {stats.archive_count} {stats.archive_count === 1 ? "achievement" : "achievements"}
          </p>
        )}
      </section>

      <section data-testid="biography-section">
        {bioLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="mx-auto h-4 rounded bg-stone-200/60 animate-pulse" style={{ width: `${88 - i * 14}%` }} />
            ))}
          </div>
        ) : bio?.biography ? (
          <p
            data-testid="biography-text"
            className="text-center font-serif text-lg md:text-2xl font-semibold leading-relaxed tracking-tight text-stone-800"
          >
            {bio.biography}
          </p>
        ) : (
          <p className="text-center text-sm text-stone-400">
            The story begins with the first capture — pin finished work to the Archive and curiosities to the Brain.
          </p>
        )}
      </section>

      {interests?.nodes?.length > 0 && (
        <section data-testid="interests-section" className="space-y-4">
          <Label>The constellation of interests</Label>
          <InterestGraph data={interests} avatarUrl={avatar} initial={(first || "B")[0].toUpperCase()} />
          <p className="text-center text-[11px] text-stone-400">Tap a star to wander that topic in the Brain.</p>
        </section>
      )}

      <section data-testid="achievements-section" className="space-y-5">
        <Label>Top achievements</Label>
        {archive?.length ? (
          <>
            <div className="space-y-3">
              {archive.map((e) => {
                const img = fileUrl(e.image_path);
                return (
                  <Link
                    key={e.id}
                    to={`/entry/${e.id}`}
                    data-testid={`home-achievement-${e.id}`}
                    className="flex items-center gap-4 rounded-2xl glass-card px-4 py-3.5 active:scale-[0.98] transition-transform"
                  >
                    {img ? (
                      <img src={img} alt="" loading="lazy" className="h-14 w-14 rounded-xl object-cover shrink-0" />
                    ) : (
                      <span className={`h-14 w-14 rounded-xl bg-gradient-to-br ${gradientFor(e.id)} flex items-center justify-center shrink-0`}>
                        <Award className="h-5 w-5 text-white/85" />
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-serif text-lg font-semibold text-stone-800">{e.title}</span>
                      {(e.description || e.summary) && (
                        <span className="block truncate text-xs text-stone-400">{e.description || e.summary}</span>
                      )}
                    </span>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-primary" />
                  </Link>
                );
              })}
            </div>
            <div className="text-center">
              <Link
                to="/archive"
                data-testid="archive-see-all"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:opacity-80 transition-opacity"
              >
                The full archive <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </>
        ) : (
          <p className="text-center text-sm text-stone-400">
            Nothing pinned yet — finish something, then{" "}
            <Link to="/brain" className="font-semibold text-primary">turn a discovery into an achievement</Link>.
          </p>
        )}
      </section>

      <section data-testid="brain-chapter" className="rounded-3xl glass-card p-10 text-center space-y-3">
        <BrainIcon className="h-7 w-7 mx-auto text-primary" strokeWidth={1.5} />
        <p className="font-serif text-2xl md:text-3xl tracking-tight">Follow the curiosity</p>
        <p className="text-xs text-stone-400">
          {stats ? `${stats.brain_count} ${stats.brain_count === 1 ? "thing" : "things"} being explored right now` : "—"}
        </p>
        <button
          data-testid="brain-chapter-cta"
          onClick={() => navigate("/brain")}
          className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-7 py-3 text-sm font-semibold shadow-md shadow-primary/25 active:scale-95 transition-transform"
        >
          Enter the Brain <ArrowRight className="h-4 w-4" />
        </button>
      </section>

      <footer className="pt-2 pb-4 text-center">
        <Link
          to="/shortcut"
          data-testid="shortcut-link"
          className="inline-flex items-center gap-1.5 text-[11px] font-medium text-stone-400 hover:text-primary transition-colors"
        >
          <Zap className="h-3 w-3" /> Save links from your iPhone with an Apple Shortcut
        </Link>
      </footer>
    </div>
  );
}
